import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../../routes/paths';
import { Features } from '../../../../assets/features/Features';
import { useLanguage } from '../../../../context/LanguageContext';
import { useTheme } from '../../../../context/ThemeContext';
import SEO from '../../seo/SEO';
import { seoData } from '../../../../data/seoData';

const ArbitrationPage: React.FC = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Initialize AOS animation library
    AOS.init({
      duration: 1000,
      easing: 'ease-in-out',
      once: true,
      mirror: false
    });
  }, []);

  return (
    <>
      <SEO 
        title={seoData.arbitration.title}
        description={seoData.arbitration.description}
        canonical={seoData.arbitration.canonical}
      />
      {/* Hero Section */}
      <section 
        className="arbitration-hero d-flex align-items-center" 
        style={{ 
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTJ8fGxlZ2FsfGVufDB8fDB8fHww&auto=format&fit=crop&w=1200&q=80)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '40vh',
          position: 'relative'
        }}
      >
        <div className="container">
          <div className="row">
            <div className="col-12 text-center text-white">
              <h1 
                className="display-4 fw-bold" 
                data-aos="fade-up" 
                style={{ 
                  color: '#ffffff', 
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)'
                }}
              >
                {Features.arbitrationPage.hero.title[language]}
              </h1>
              <div className="divider mx-auto my-4" data-aos="fade-up" data-aos-delay="100" style={{
                width: '80px',
                height: '4px',
                background: 'var(--primary-color, #1977cc)',
                margin: '0 auto'
              }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Clinic Information Section */}
      <section className="clinic-details py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 mb-4 mb-lg-0" data-aos="fade-right" data-aos-delay="100">
              <div className={`clinic-info p-4 rounded shadow h-100 ${theme === 'dark' ? 'bg-dark text-light' : 'bg-white'}`}>
                <div className="d-flex align-items-center mb-4">
                  <div className="icon-box me-3">
                    <i className="bi bi-building text-primary fs-1"></i>
                  </div>
                  <div>
                    <h3 className="h4 mb-1 fw-bold">{Features.arbitrationPage.clinicInfo.title[language]}</h3>
                    <p className={`mb-0 ${theme === 'dark' ? 'text-light' : 'text-muted'}`}>{Features.arbitrationPage.clinicInfo.subtitle[language]}</p>
                  </div>
                </div>
                
                <p className="mb-4">
                  {Features.arbitrationPage.clinicInfo.description[language]}
                </p>
                
                <Link to={ROUTES.ABOUT_US} className="btn btn-primary rounded-pill px-4">
                  <i className="bi bi-info-circle me-2"></i>
                  {Features.arbitrationPage.clinicInfo.seeMoreButton[language]}
                </Link>
              </div>
            </div>
            
            <div className="col-lg-6" data-aos="fade-left" data-aos-delay="200">
              <div className={`hours-info p-4 rounded shadow h-100 ${theme === 'dark' ? 'bg-dark text-light' : 'bg-white'}`}>
                <div className="d-flex align-items-center mb-4">
                  <div className="icon-box me-3">
                    <i className="bi bi-clock text-primary fs-1"></i>
                  </div>
                  <div>
                    <h3 className="h4 mb-1 fw-bold">{Features.arbitrationPage.openingHours.title[language]}</h3>
                    <p className={`mb-0 ${theme === 'dark' ? 'text-light' : 'text-muted'}`}>{Features.arbitrationPage.openingHours.subtitle[language]}</p>
                  </div>
                </div>
                
                <div className="hours-table">
                  <div className="row py-2 border-bottom align-items-center" style={{ backgroundColor: theme === 'dark' ? 'rgba(25, 119, 204, 0.15)' : 'rgba(25, 119, 204, 0.05)' }}>
                    <div className="col-4 fw-bold">{Features.arbitrationPage.openingHours.dayLabel[language]}</div>
                    <div className="col-8 text-end">{Features.arbitrationPage.openingHours.timeLabel[language]}</div>
                  </div>
                  
                  <div className="row py-2 border-bottom align-items-center">
                    <div className="col-4">
                      <span className="day-badge bg-primary text-white px-2 py-1 rounded-pill">{Features.arbitrationPage.openingHours.days.monday[language]}</span>
                    </div>
                    <div className="col-8 text-end">{Features.arbitrationPage.openingHours.hours.weekdays[language]}</div>
                  </div>
                  
                  <div className="row py-2 border-bottom align-items-center">
                    <div className="col-4">
                      <span className="day-badge bg-primary text-white px-2 py-1 rounded-pill">{Features.arbitrationPage.openingHours.days.tuesday[language]}</span>
                    </div>
                    <div className="col-8 text-end">{Features.arbitrationPage.openingHours.hours.weekdays[language]}</div>
                  </div>
                  
                  <div className="row py-2 border-bottom align-items-center">
                    <div className="col-4">
                      <span className="day-badge bg-primary text-white px-2 py-1 rounded-pill">{Features.arbitrationPage.openingHours.days.wednesday[language]}</span>
                    </div>
                    <div className="col-8 text-end">{Features.arbitrationPage.openingHours.hours.weekdays[language]}</div>
                  </div>
                  
                  <div className="row py-2 border-bottom align-items-center">
                    <div className="col-4">
                      <span className="day-badge bg-primary text-white px-2 py-1 rounded-pill">{Features.arbitrationPage.openingHours.days.thursday[language]}</span>
                    </div>
                    <div className="col-8 text-end">{Features.arbitrationPage.openingHours.hours.weekdays[language]}</div>
                  </div>
                  
                  <div className="row py-2 border-bottom align-items-center">
                    <div className="col-4">
                      <span className="day-badge bg-primary text-white px-2 py-1 rounded-pill">{Features.arbitrationPage.openingHours.days.friday[language]}</span>
                    </div>
                    <div className="col-8 text-end">{Features.arbitrationPage.openingHours.hours.weekdays[language]}</div>
                  </div>
                  
                  <div className="row py-2 border-bottom align-items-center">
                    <div className="col-4">
                      <span className="day-badge bg-success text-white px-2 py-1 rounded-pill">{Features.arbitrationPage.openingHours.days.saturday[language]}</span>
                    </div>
                    <div className="col-8 text-end">{Features.arbitrationPage.openingHours.hours.saturday[language]}</div>
                  </div>
                  
                  <div className="row py-2 align-items-center">
                    <div className="col-4">
                      <span className="day-badge bg-danger text-white px-2 py-1 rounded-pill">{Features.arbitrationPage.openingHours.days.sunday[language]}</span>
                    </div>
                    <div className="col-8 text-end">{Features.arbitrationPage.openingHours.hours.sunday[language]}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="row mt-4">
            <div className="col-12" data-aos="fade-up" data-aos-delay="300">
              <div className="contact-card bg-primary text-white p-4 rounded shadow">
                <div className="row align-items-center">
                  <div className="col-lg-8 text-center text-lg-start mb-3 mb-lg-0">
                    <h3 className="h4 mb-2">{Features.arbitrationPage.contactCard.title[language]}</h3>
                    <p className="lead mb-0">{Features.arbitrationPage.contactCard.description[language]}</p>
                  </div>
                  <div className="col-lg-4 text-center text-lg-end">
                    <Link to="/#contact" className="btn btn-light rounded-pill px-4 py-2">
                      <i className="bi bi-chat-dots me-2"></i>
                      {Features.arbitrationPage.contactCard.contactButton[language]}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Arbitration Information Section */}
      <section className={`arbitration-info py-5 ${theme === 'dark' ? 'bg-dark text-light' : 'bg-light'}`}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 text-center mb-5" data-aos="fade-up">
              <h2 className="fw-bold text-primary">{Features.arbitrationPage.arbitrationInfo.title[language]}</h2>
              <div className="divider mx-auto my-3" style={{
                width: '60px',
                height: '3px',
                background: 'var(--primary-color, #1977cc)',
                margin: '0 auto'
              }}></div>
            </div>
            
            <div className="col-lg-8" data-aos="fade-up">
              <div className={`info-card p-5 rounded shadow ${theme === 'dark' ? 'bg-secondary text-light' : 'bg-white'}`} style={{ 
                backgroundColor: theme === 'dark' ? '#2a2c2e' : '' 
              }}>
                <p className="mb-4 lead">
                  {Features.arbitrationPage.arbitrationInfo.introduction[language]}
                </p>
                
                <div className={`quote p-4 rounded mb-4 ${theme === 'dark' ? 'bg-dark' : 'bg-light'}`} style={{
                  borderLeft: '4px solid var(--primary-color, #1977cc)'
                }}>
                  <p className="fst-italic mb-0">
                    {Features.arbitrationPage.arbitrationInfo.quote[language]}
                  </p>
                </div>
                
                <div className={`contact-info p-4 border-start border-primary border-3 rounded ${theme === 'dark' ? 'bg-dark' : 'bg-light'}`}>
                  <h3 className="h5 mb-3 text-primary">{Features.arbitrationPage.arbitrationInfo.centerName[language]}</h3>
                  
                  <div className="d-flex mb-3">
                    <div className="icon me-3">
                      <i className="bi bi-geo-alt-fill text-primary fs-4"></i>
                    </div>
                    <div>
                      <strong>{Features.arbitrationPage.arbitrationInfo.address.label[language]}</strong><br />
                      {Features.arbitrationPage.arbitrationInfo.address.value[language]}
                    </div>
                  </div>
                  
                  <div className="d-flex mb-3">
                    <div className="icon me-3">
                      <i className="bi bi-telephone-fill text-primary fs-4"></i>
                    </div>
                    <div>
                      <strong>{Features.arbitrationPage.arbitrationInfo.phone.label[language]}</strong> {Features.arbitrationPage.arbitrationInfo.phone.value[language]}
                      <span
                        className="phone-info-icon"
                        data-tooltip-text={language === 'en' ? 'Call to a national landline' : 'Chamada para a rede fixa nacional'}
                      >
                        <i className="bi bi-info-circle-fill"></i>
                      </span><br />
                      <strong>{Features.arbitrationPage.arbitrationInfo.fax.label[language]}</strong> {Features.arbitrationPage.arbitrationInfo.fax.value[language]}
                    </div>
                  </div>
                  
                  <div className="d-flex mb-3">
                    <div className="icon me-3">
                      <i className="bi bi-envelope-fill text-primary fs-4"></i>
                    </div>
                    <div>
                      <strong>{Features.arbitrationPage.arbitrationInfo.email.label[language]}</strong> <a href={`mailto:${Features.arbitrationPage.arbitrationInfo.email.value[language]}`} className={`${theme === 'dark' ? 'text-light' : ''} text-decoration-none`}>{Features.arbitrationPage.arbitrationInfo.email.value[language]}</a>
                    </div>
                  </div>
                  
                  <div className="d-flex">
                    <div className="icon me-3">
                      <i className="bi bi-globe text-primary fs-4"></i>
                    </div>
                    <div>
                      <strong>{Features.arbitrationPage.arbitrationInfo.website.label[language]}</strong> <a href={`http://${Features.arbitrationPage.arbitrationInfo.website.value[language]}`} target="_blank" rel="noopener noreferrer" className={`${theme === 'dark' ? 'text-light' : ''} text-decoration-none`}>{Features.arbitrationPage.arbitrationInfo.website.value[language]}</a>
                    </div>
                  </div>
                </div>
                
                <p className="mt-4 mb-2">
                  {Features.arbitrationPage.arbitrationInfo.moreInfo[language]}
                </p>
                
                <p className="text-end mt-4">
                  <small className={theme === 'dark' ? 'text-light opacity-75' : 'text-muted'}>{Features.arbitrationPage.arbitrationInfo.date[language]}</small>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ArbitrationPage; 